import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TrialRecap {
  currency: string;
  trialStart: string;
  generatedAt: string;
  scans: { total: number; manualCount: number; secondsSaved: number };
  money: {
    totalLogged: number;
    expenseCount: number;
    topCategory: { name: string; amount: number; percent: number } | null;
    budgetAdherence: { daysUnder: number; daysTotal: number } | null;
  };
  goals: {
    goalsSaved: number;
    goalsCreated: number;
    milestonesHit: number;
    currentStreak: number;
  };
  fennie: {
    stage: string;
    healthScore: number;
    aiInsights: number;
    aiActions: number;
  };
}

const CACHE_TTL_MS = 10 * 60 * 1000;
const memCache = new Map<string, { at: number; data: TrialRecap }>();

export function useTrialRecap(enabled: boolean) {
  const { user } = useAuth();
  const [data, setData] = useState<TrialRecap | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !user) return;
    const cached = memCache.get(user.id);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setData(cached.data);
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase.functions
      .invoke('get-trial-recap', { body: {} })
      .then(({ data: res, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('get-trial-recap error:', error);
          setLoading(false);
          return;
        }
        memCache.set(user.id, { at: Date.now(), data: res as TrialRecap });
        setData(res as TrialRecap);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, user]);

  return { data, loading };
}
